import { useState } from 'react';
import { Plus, Trash2, GripVertical, Briefcase, FileText, FolderOpen } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import {
  useDealTypes,
  useCreateDealType,
  useUpdateDealType,
  useDeleteDealType,
  useDealTypeQuestions,
  useSetDealTypeQuestions,
} from '@/hooks/useDealTypes';
import { useQualifyingQuestions } from '@/hooks/useQualifyingQuestions';
import { Button, Card, CardBody, CardHeader, EmptyState, Input, Modal, Skeleton } from '@solid/ui';
import { Badge } from '@/components/ui/Badge';
import type { DealType, QualifyingQuestion } from '@/types';

// Get icon component from name
function getIconComponent(iconName?: string) {
  if (!iconName) return Briefcase;
  const icons = LucideIcons as Record<string, unknown>;
  const icon = icons[iconName];
  if (typeof icon === 'object' && icon !== null && '$$typeof' in icon) {
    return icon as React.ComponentType<{ className?: string }>;
  }
  return Briefcase;
}

export default function DealTypeSettings() {
  const { data: dealTypes, isLoading } = useDealTypes();
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  if (isLoading) {
    return <Skeleton className="h-64 w-full rounded-xl" />;
  }

  const sortedTypes = [...(dealTypes || [])].sort((a, b) => a.position - b.position);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Project Types"
          subtitle="Configure project types for the submission wizard, including template associations and qualifying questions."
          actions={
            <Button size="sm" onClick={() => setShowAdd(true)}>
              <Plus className="w-3.5 h-3.5" />
              Add Project Type
            </Button>
          }
        />

        <CardBody className="space-y-2">
          {sortedTypes.length === 0 ? (
            <EmptyState icon={<Briefcase />} title="No project types configured" />
          ) : (
            sortedTypes.map((dealType) => (
              <DealTypeRow
                key={dealType.id}
                dealType={dealType}
                isEditing={editingId === dealType.id}
                onEdit={() => setEditingId(dealType.id)}
                onClose={() => setEditingId(null)}
              />
            ))
          )}
        </CardBody>
      </Card>

      {showAdd && (
        <AddDealTypeDialog
          open={showAdd}
          onOpenChange={setShowAdd}
          nextPosition={sortedTypes.length}
        />
      )}
    </div>
  );
}

function DealTypeRow({
  dealType,
  isEditing,
  onEdit,
  onClose,
}: {
  dealType: DealType;
  isEditing: boolean;
  onEdit: () => void;
  onClose: () => void;
}) {
  const [form, setForm] = useState({
    name: dealType.name,
    description: dealType.description || '',
    icon: dealType.icon || '',
    templateDocId: dealType.templateDocId || '',
    templateFolderId: dealType.templateFolderId || '',
  });
  const updateDealType = useUpdateDealType();
  const deleteDealType = useDeleteDealType();

  const IconComponent = getIconComponent(dealType.icon);

  const handleSave = async () => {
    await updateDealType.mutateAsync({
      id: dealType.id,
      data: {
        name: form.name,
        description: form.description || undefined,
        icon: form.icon || undefined,
        templateDocId: form.templateDocId || undefined,
        templateFolderId: form.templateFolderId || undefined,
      },
    });
    onClose();
  };

  const handleDelete = async () => {
    if (!confirm(`Delete "${dealType.name}"? This cannot be undone.`)) return;
    await deleteDealType.mutateAsync(dealType.id);
  };

  return (
    <div className="rounded-lg bg-grey-50 group">
      <div className="flex items-start gap-3 p-3">
        <GripVertical className="w-4 h-4 text-grey-300 cursor-grab mt-2 flex-shrink-0" />

        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-grey-200 text-grey-500">
          <IconComponent className="h-5 w-5" />
        </div>

        {isEditing ? (
          <div className="flex-1 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <input
                className="input py-1.5 px-2 text-sm"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Name"
              />
              <input
                className="input py-1.5 px-2 text-sm"
                value={form.icon}
                onChange={(e) => setForm({ ...form, icon: e.target.value })}
                placeholder="Icon name (e.g. Briefcase)"
              />
            </div>
            <input
              className="input py-1.5 px-2 text-sm w-full"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Description"
            />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-grey-500 mb-1 flex items-center gap-1">
                  <FileText className="w-3 h-3" />
                  Template Doc ID
                </label>
                <input
                  className="input py-1.5 px-2 text-sm w-full font-mono text-xs"
                  value={form.templateDocId}
                  onChange={(e) => setForm({ ...form, templateDocId: e.target.value })}
                  placeholder="Google Doc ID"
                />
              </div>
              <div>
                <label className="text-xs text-grey-500 mb-1 flex items-center gap-1">
                  <FolderOpen className="w-3 h-3" />
                  Template Folder ID
                </label>
                <input
                  className="input py-1.5 px-2 text-sm w-full font-mono text-xs"
                  value={form.templateFolderId}
                  onChange={(e) => setForm({ ...form, templateFolderId: e.target.value })}
                  placeholder="Google Drive Folder ID"
                />
              </div>
            </div>

            {/* Questions section */}
            <DealTypeQuestionsManager dealTypeId={dealType.id} />

            <div className="flex items-center gap-2 pt-2">
              <Button size="sm" onClick={handleSave} loading={updateDealType.isPending}>
                Save
              </Button>
              <Button size="sm" variant="ghost" onClick={onClose}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium text-secondary">{dealType.name}</p>
                {!dealType.isActive && (
                  <Badge variant="default" size="sm">Inactive</Badge>
                )}
              </div>
              {dealType.description && (
                <p className="text-xs text-grey-500 mt-0.5">{dealType.description}</p>
              )}
              <div className="flex items-center gap-3 mt-1.5">
                {dealType.templateDocId && (
                  <span className="inline-flex items-center gap-1 text-xs text-grey-400">
                    <FileText className="w-3 h-3" />
                    Template linked
                  </span>
                )}
                {dealType.questions && dealType.questions.length > 0 && (
                  <span className="inline-flex items-center gap-1 text-xs text-grey-400">
                    {dealType.questions.length} question{dealType.questions.length !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </div>

            <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 flex-shrink-0">
              <button
                onClick={onEdit}
                className="p-1.5 rounded hover:bg-grey-200 text-grey-400 hover:text-grey-600"
              >
                <span className="text-xs">Edit</span>
              </button>
              <button
                onClick={handleDelete}
                className="p-1.5 rounded hover:bg-grey-200 text-grey-400 hover:text-error"
                aria-label={`Delete ${dealType.name}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function DealTypeQuestionsManager({ dealTypeId }: { dealTypeId: string }) {
  const { data: assignedQuestions, isLoading: loadingAssigned } = useDealTypeQuestions(dealTypeId);
  const { data: allQuestions, isLoading: loadingAll } = useQualifyingQuestions();
  const setQuestions = useSetDealTypeQuestions();

  if (loadingAssigned || loadingAll) {
    return <Skeleton className="h-20 w-full rounded" />;
  }

  const assignedIds = new Set((assignedQuestions || []).map((q) => q.id));

  const toggleQuestion = async (questionId: string) => {
    const currentIds = (assignedQuestions || []).map((q) => q.id);
    let newIds: string[];

    if (assignedIds.has(questionId)) {
      newIds = currentIds.filter((id) => id !== questionId);
    } else {
      newIds = [...currentIds, questionId];
    }

    await setQuestions.mutateAsync({ id: dealTypeId, questionIds: newIds });
  };

  return (
    <div className="border border-grey-200 rounded-lg p-3">
      <p className="text-xs font-medium text-grey-600 mb-2">Qualifying Questions</p>
      {(allQuestions || []).length === 0 ? (
        <p className="text-xs text-grey-400">No qualifying questions available</p>
      ) : (
        <div className="space-y-1 max-h-48 overflow-y-auto">
          {(allQuestions || []).map((question: QualifyingQuestion) => {
            const isAssigned = assignedIds.has(question.id);
            return (
              <label
                key={question.id}
                className="flex items-start gap-2 p-2 rounded hover:bg-grey-100 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={isAssigned}
                  onChange={() => toggleQuestion(question.id)}
                  className="mt-0.5"
                  disabled={setQuestions.isPending}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-secondary">{question.question}</p>
                  <p className="text-[10px] text-grey-400 mt-0.5">{question.importance}</p>
                </div>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AddDealTypeDialog({
  open,
  onOpenChange,
  nextPosition,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  nextPosition: number;
}) {
  const [form, setForm] = useState({
    name: '',
    description: '',
    icon: '',
    templateDocId: '',
    templateFolderId: '',
  });
  const createDealType = useCreateDealType();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await createDealType.mutateAsync({
      name: form.name,
      description: form.description || undefined,
      icon: form.icon || undefined,
      position: nextPosition,
      templateDocId: form.templateDocId || undefined,
      templateFolderId: form.templateFolderId || undefined,
    });
    onOpenChange(false);
    setForm({ name: '', description: '', icon: '', templateDocId: '', templateFolderId: '' });
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Add Project Type"
      description="Create a new project type for the submission wizard."
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" form="add-deal-type-form" loading={createDealType.isPending}>
            Add Project Type
          </Button>
        </>
      }
    >
      <form id="add-deal-type-form" onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Name"
          required
          placeholder="e.g. Website Development"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
        <Input
          label="Description (optional)"
          placeholder="Brief description of this project type"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />
        <Input
          label="Icon Name (optional)"
          placeholder="Lucide icon name (e.g. Globe, Code, Palette)"
          value={form.icon}
          onChange={(e) => setForm({ ...form, icon: e.target.value })}
        />
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Template Doc ID"
            placeholder="Google Doc ID"
            value={form.templateDocId}
            onChange={(e) => setForm({ ...form, templateDocId: e.target.value })}
          />
          <Input
            label="Template Folder ID"
            placeholder="Google Drive Folder ID"
            value={form.templateFolderId}
            onChange={(e) => setForm({ ...form, templateFolderId: e.target.value })}
          />
        </div>
      </form>
    </Modal>
  );
}
